import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card } from '@careandkey/shared';
import { supabase } from '../lib/supabase';
import { Layout, Topbar } from '../components/Layout';
import { ClientForm, type ClientRow } from '../components/EntityForms';

type Row = ClientRow;

export function ClientsList() {
  const { t } = useTranslation();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Row | null>(null);

  const load = useCallback(async () => {
    const r = await supabase.from('clients').select('id,name,client_type,email,phone,notes').is('deleted_at', null).order('name');
    setRows((r.data as unknown as Row[]) ?? []);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (r: Row) => { setEditing(r); setFormOpen(true); };

  return (
    <Layout header={<Topbar actions={<Button onClick={openNew}>+ {t('form.newClient')}</Button>} />}>
      <div className="px-7 pt-6">
        <h1 className="text-[30px] font-semibold tracking-[-0.02em]">{t('nav.clients')}</h1>
      </div>
      <div className="px-7 pb-7 pt-[18px]">
        <Card className="overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="text-left text-xs text-muted">
                  <th className="px-5 py-3 font-medium">{t('col.name')}</th>
                  <th className="px-2 py-3 font-medium">{t('col.type')}</th>
                  <th className="px-2 py-3 font-medium">{t('col.email')}</th>
                  <th className="px-2 py-3 font-medium">{t('col.phone')}</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={5} className="px-5 py-6 text-muted">{t('common.loading')}</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={5} className="px-5 py-6 text-muted">{t('common.noData')}</td></tr>
                ) : (
                  rows.map((r) => (
                    <tr key={r.id} className="border-t border-divider hover:bg-bg">
                      <td className="px-5 py-3.5 font-medium">{r.name}</td>
                      <td className="px-2 py-3.5 text-muted">{t('clientType.' + r.client_type)}</td>
                      <td className="px-2 py-3.5 font-mono text-xs text-muted">{r.email ?? '—'}</td>
                      <td className="px-2 py-3.5 font-mono text-xs text-muted">{r.phone ?? '—'}</td>
                      <td className="px-5 py-3.5 text-right">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(r)}>{t('form.edit')}</Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
      <ClientForm open={formOpen} onClose={() => setFormOpen(false)} onSaved={load} initial={editing} />
    </Layout>
  );
}
